import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import AdminLayout from '../components/AdminLayout';
import Loader from '../components/Loader';
import '../components/AdminLayout.css';
import {
    LineChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
    getSystemWalletStats,
    getTransactionStatsByDate,
    formatETH,
    formatUSD
} from '../services/walletAnalyticsService';
import { getSystemReferralStats } from '../services/referralService';

function AdminDashboard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [userCount, setUserCount] = useState(0);
    const [walletStats, setWalletStats] = useState({
        total_wallets: 0,
        total_crypto_balance_eth: 0,
        total_fiat_balance_usd: 0,
        total_transactions: 0,
        transactions_today: 0,
        total_deposits_eth: 0,
        total_withdrawals_eth: 0,
        total_transfers_usd: 0,
        active_wallets_today: 0
    });
    const [transactionHistory, setTransactionHistory] = useState([]);
    const [referralStats, setReferralStats] = useState({
        total_referred_users: 0,
        total_bonus_paid_usd: 0
    });

    useEffect(() => {
        const userRole = user?.user_metadata?.role || localStorage.getItem('role');
        if (String(userRole) !== '4') {
            navigate('/welcome');
            return;
        }
        fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, navigate]);

    const fetchDashboardData = async () => {
        try {
            const { count } = await supabase
                .from('users')
                .select('*', { count: 'exact', head: true });
            setUserCount(count || 0);

            const walletStatsResponse = await getSystemWalletStats();
            if (walletStatsResponse.success && walletStatsResponse.data) {
                setWalletStats(walletStatsResponse.data);
            }

            const txHistoryResponse = await getTransactionStatsByDate(7);
            if (txHistoryResponse.success) {
                setTransactionHistory(txHistoryResponse.data);
            }

            const referralResponse = await getSystemReferralStats();
            if (referralResponse.success && referralResponse.data) {
                setReferralStats(referralResponse.data);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const StatCard = ({ title, value, sublabel, icon, colorClass, onClick }) => (
        <div
            className={`stat-card${onClick ? ' stat-card-link' : ''}`}
            onClick={onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={onClick ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick() : undefined}
        >
            <div className={`stat-icon ${colorClass}`}>
                <i className={`bx ${icon}`}></i>
            </div>
            <div className="stat-content">
                <div className="stat-label">{title}</div>
                <div className="stat-value">{value}</div>
                {sublabel && <div className="stat-change">{sublabel}</div>}
            </div>
        </div>
    );

    if (loading) {
        return (
            <AdminLayout>
                <Loader />
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        <i className="bx bx-chart"></i>
                        Dashboard Overview
                    </h1>
                    <p className="page-subtitle">Monitor your Ethereum wallet platform at a glance</p>
                </div>
            </div>

            {/* Key Metrics */}
            <div className="stats-grid">
                <StatCard
                    title="Total Users"
                    value={userCount}
                    icon="bx-group"
                    colorClass="primary"
                />
                <StatCard
                    title="Total Wallets"
                    value={walletStats.total_wallets}
                    icon="bx-wallet"
                    colorClass="info"
                />
                <StatCard
                    title="Total Transactions"
                    value={walletStats.total_transactions}
                    icon="bx-transfer"
                    colorClass="warning"
                />
                <StatCard
                    title="Active Today"
                    value={walletStats.active_wallets_today || 0}
                    icon="bx-pulse"
                    colorClass="success"
                />
                <StatCard
                    title="Referral Signups"
                    value={referralStats.total_referred_users}
                    sublabel={`${formatUSD(referralStats.total_bonus_paid_usd)} paid out`}
                    icon="bx-gift"
                    colorClass="primary"
                    onClick={() => navigate('/admin/referrals')}
                />
            </div>

            {/* Wallet Statistics */}
            <div className="admin-card">
                <div className="card-header">
                    <h3 className="card-title">
                        <i className='bx bx-wallet'></i>
                        Wallet Statistics
                    </h3>
                </div>
                <div className="stats-grid">
                    <div>
                        <div className="stat-label">ETH Held (Custodial)</div>
                        <div className="stat-value">{formatETH(walletStats.total_crypto_balance_eth)} ETH</div>
                    </div>
                    <div>
                        <div className="stat-label">Total User USD Balance</div>
                        <div className="stat-value">{formatUSD(walletStats.total_fiat_balance_usd)}</div>
                    </div>
                    <div>
                        <div className="stat-label">Total On-Chain Deposits</div>
                        <div className="stat-value">{formatUSD(walletStats.total_deposits_eth * 3000)}</div>
                        <div className="stat-sublabel" style={{ fontSize: '0.75rem', color: '#7f8c8d', marginTop: '0.25rem' }}>USD equivalent at avg rate</div>
                    </div>
                    <div>
                        <div className="stat-label">Total Transfers</div>
                        <div className="stat-value">{formatUSD(walletStats.total_transfers_usd)}</div>
                    </div>
                </div>
            </div>

            {/* Transaction History Chart */}
            {transactionHistory.length > 0 && (
                <div className="admin-card">
                    <div className="card-header">
                        <h3 className="card-title">Transaction History (Last 7 Days)</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={350}>
                        <LineChart data={transactionHistory}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                            <XAxis dataKey="date" stroke="#8884d8" style={{ fontSize: '12px' }} />
                            <YAxis stroke="#8884d8" style={{ fontSize: '12px' }} />
                            <Tooltip
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', padding: '10px' }}
                                formatter={(value, name) => {
                                    const labels = { deposits: 'Deposits', withdrawals: 'Withdrawals', transfers: 'Transfers' };
                                    return [`${value} transaction${value !== 1 ? 's' : ''}`, labels[name] || name];
                                }}
                            />
                            <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="line" />
                            <Line type="monotone" dataKey="deposits" stroke="#1bc5bd" strokeWidth={2} name="Deposits" dot={{ r: 4 }} activeDot={{ r: 6 }} />
                            <Line type="monotone" dataKey="withdrawals" stroke="#f64e60" strokeWidth={2} name="Withdrawals" dot={{ r: 4 }} activeDot={{ r: 6 }} />
                            <Line type="monotone" dataKey="transfers" stroke="#3699ff" strokeWidth={2} name="Transfers" dot={{ r: 4 }} activeDot={{ r: 6 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </AdminLayout>
    );
}

export default AdminDashboard;
